import React, { useEffect, useMemo } from "react";
import Select from "react-select";
import { COUNTRIES } from "./Countries";

// Reliable flag image
function getFlagUrl(code) {
  return `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
}

export default function CountryStep({ formData, setFormData, onNext }) {
  const { countryFrom, countryTo } = formData || {};

  // Sort countries
  const sortedCountries = useMemo(() => {
    return [...COUNTRIES].sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  // Format options (FLAG IMAGE + NAME ONLY)
  const countryOptions = useMemo(() => {
    return sortedCountries.map((c) => ({
      value: c.code,
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src={getFlagUrl(c.code)}
            alt={c.name}
            style={{
              width: 20,
              height: 14,
              objectFit: "cover",
              borderRadius: 2,
            }}
          />
          <span>{c.name}</span>
        </div>
      ),
      currency: c.currency,
    }));
  }, [sortedCountries]);

  const fromObj = COUNTRIES.find((c) => c.code === countryFrom);
  const toObj = COUNTRIES.find((c) => c.code === countryTo);

  // Sync sender
  useEffect(() => {
    if (!countryFrom || !fromObj) return;

    setFormData((prev) => ({
      ...prev,
      currency: fromObj.currency,
      sender: {
        ...prev.sender,
        country: countryFrom,
      },
    }));
  }, [countryFrom, fromObj, setFormData]);

  // Sync recipient
  useEffect(() => {
    if (!countryTo || !toObj) return;

    setFormData((prev) => ({
      ...prev,
      recipientCurrency: toObj.currency,
      destinationCountry: toObj.code,
    }));
  }, [countryTo, toObj, setFormData]);

  const sameCountry = countryFrom && countryTo && countryFrom === countryTo;

  const canContinue = countryFrom && countryTo && !sameCountry;

  return (
    <div className="space-y-6 font-roboto w-full">
      {/* HEADER */}
      <div>
        <h2 className="text-lg font-semibold">Détails de la transaction</h2>
        <p className="text-sm text-gray-500">Choisissez les pays.</p>
      </div>

      {/* FROM */}
      <div>
        <label className="block mb-2 font-medium">Envoyer de *</label>

        <Select
          options={countryOptions}
          value={countryOptions.find((o) => o.value === countryFrom)}
          onChange={(selected) =>
            setFormData((prev) => ({
              ...prev,
              countryFrom: selected?.value,
              ...(prev.countryTo === selected?.value ? { countryTo: "" } : {}),
            }))
          }
          placeholder="Choisir un pays..."
          className="text-sm"
          isSearchable
          formatOptionLabel={(option) => option.label}
        />

        {fromObj && (
          <p className="mt-2 text-sm">
            Devise: <b>{fromObj.currency}</b>
          </p>
        )}
      </div>

      {/* TO */}
      <div>
        <label className="block mb-2 font-medium">Envoyer à *</label>

        <Select
          options={countryOptions.filter((c) => c.value !== countryFrom)}
          value={countryOptions.find((o) => o.value === countryTo)}
          onChange={(selected) =>
            setFormData((prev) => ({
              ...prev,
              countryTo: selected?.value,
            }))
          }
          placeholder="Choisir un pays..."
          className="text-sm"
          isSearchable
          formatOptionLabel={(option) => option.label}
        />

        {sameCountry && (
          <p className="text-red-500 mt-2">Les pays doivent être différents.</p>
        )}

        {toObj && !sameCountry && (
          <p className="mt-2 text-sm">
            Devise: <b>{toObj.currency}</b>
          </p>
        )}
      </div>

      {/* SUMMARY */}
      {(fromObj || toObj) && (
        <div className="bg-gray-100 p-4 rounded-xl text-sm">
          <div className="mb-2 font-medium">Résumé</div>

          <div className="flex justify-between">
            <span>De: {fromObj ? fromObj.name : "—"}</span>

            <span>À: {toObj ? toObj.name : "—"}</span>
          </div>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={onNext}
        disabled={!canContinue}
        className="w-full py-3 rounded-xl bg-black text-white disabled:opacity-50"
      >
        Continuer
      </button>
    </div>
  );
}
